import type { ReactNode } from 'react';
import type { ZodError } from 'zod';
import type { FileButtonUploadFn } from '../../../components/FileButton/FileButton';
import type { RelationShowFn } from '../../../components/RelationCard/RelationCard';
import type { NewMediaUploadFn } from '../../../hooks/api/useMediaUpload';
import type {
  ContentObject,
  ContentTypePropertyConfig,
  Media,
  RelationWithoutHydration,
} from '../../../lib/flotiq-client/types/api-response';

import { use, useCallback, useEffect, useId, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

import { getMediaUrl } from '../../../lib/flotiq-client/api-helpers';
import FlotiqPlugins from '../../../lib/flotiq-plugins/flotiqPluginsRegistry';
import { FormRelationAfterSubmitEvent } from '../../../lib/flotiq-plugins/plugin-events/FormRelationAfterSubmitEvent';
import {
  getObjectDataFromDataUrl,
  getTestProps,
  isRecord,
} from '../../../lib/helpers';
import { RolePermissions } from '../../../lib/rolePermissions';

import Button from '../../../components/Button/Button';
import CreateObjectContentModal from '../../../components/CreateObjectContentModal/CreateObjectContentModal';
import EditObjectContentModal from '../../../components/EditObjectContentModal/EditObjectContentModal';
import FieldEditorsIndicator from '../../../components/FieldEditorsIndicator/FieldEditorsIndicator';
import FileButton from '../../../components/FileButton/FileButton';
import HelpErrorTextsTemplate from '../../../components/HelpErrorTextsTemplate/HelpErrorTextsTemplate';
import LinkObjectContentModal from '../../../components/LinkObjectContentModal/LinkObjectContentModal';
import Loader from '../../../components/Loader/Loader';
import PickTypeContentModal from '../../../components/PickTypeContentModal/PickTypeContentModal';
import DraggableRelationCard from '../../../components/RelationCard/DraggableRelationCard/DraggableRelationCard';
import RelationCard from '../../../components/RelationCard/RelationCard';
import RequiredTemplate from '../../../components/RequiredTemplate/RequiredTemplate';
import Tooltip from '../../../components/Tooltip/Tooltip';

import { useFeaturedImages } from '../../../hooks/api/useFeaturedImages';
import useMediaUpload from '../../../hooks/api/useMediaUpload';
import { useRelations } from '../../../hooks/api/useRelations';
import useDebounceCallback from '../../../hooks/useDebounceCallback';
import useObjectStatusChange from '../../../hooks/useObjectStatusChange/useObjectStatusChange';

import ContentObjectFormContext from '../../../contexts/ContentObjectFormContext';
import { defaultButtonsProps, useModals } from '../../../contexts/ModalContext';
import UserContext from '../../../contexts/UserContext';
import { WebsocketContext } from '../../../contexts/WebsocketContext';

import useYjsField from '../../collaboration/useYjsField';
import { useFieldContext, useFormContext } from '../../form';
import { useFieldErrors } from '../../useFieldErrors';

interface RelationFieldProps {
  /**
   * Relation field label above relations and buttons
   */
  label?: ReactNode;
  /**
   * If relation field is required
   */
  required?: boolean;
  /**
   * Validation relations object
   */
  validation: ContentTypePropertyConfig['validation'];
  /**
   * Help text under relations and buttons
   */
  helpText?: ReactNode;
  /**
   * If field is disabled
   */
  disabled?: boolean;
  /**
   * If field is readonly (non-interactive but without disabled classes)
   */
  readonly?: boolean;
  /**
   * On media upload callback
   */
  onMediaUpload?: NewMediaUploadFn;
  /**
   * Additional container classes
   */
  additionalClasses?: string;
  /**
   * Test id for field
   */
  testId?: string;
}

const RelationField = ({
  label = '',
  required = false,
  validation,
  helpText = '',
  disabled = false,
  readonly = false,
  onMediaUpload,
  additionalClasses = '',
  testId = '',
}: RelationFieldProps) => {
  const form = useFormContext();
  const field = useFieldContext<RelationWithoutHydration[]>();
  const yjsField = useYjsField('array');
  const { awareness } = use(WebsocketContext);

  const { t } = useTranslation();
  const modal = useModals();
  const [isUploading, setIsUploading] = useState(false);
  const { removeNewMedia } = useMediaUpload();
  const { onVariantCreate, onVariantDelete, contentType, initialData } = use(
    ContentObjectFormContext,
  );
  const { permissions } = use(UserContext);

  const error = useFieldErrors(field.name);

  const actionsDisabled = disabled || readonly;

  const relationType = validation?.relationContenttype;
  const hasOnlyMedia = relationType === '_media';
  const isMultiple = validation?.relationMultiple;

  const { canCreate: canCreateRelation, canRead: canReadRelation } = useMemo(
    () =>
      !relationType
        ? { canCreate: true, canRead: true }
        : permissions.getCoPermissions(
            relationType,
            RolePermissions.PERMISSIONS_TYPES.READ,
          ),
    [permissions, relationType],
  );

  const value = useMemo(() => field.state.value || [], [field.state.value]);

  const onBlur = useCallback(() => {
    yjsField.onBlur();
    field.handleBlur();
  }, [field, yjsField]);

  const handleUpload: FileButtonUploadFn = async (files) => {
    setIsUploading(true);
    const newMedia = await onMediaUpload?.(files);
    const newRelations: RelationWithoutHydration[] = [];

    newMedia?.forEach(({ media, state }) => {
      if (!isMultiple && newRelations.length > 0) return;
      if (state === 'uploaded')
        newRelations.push({
          type: 'internal',
          dataUrl: `/api/v1/content/_media/${media.id}`,
        });
    });

    if (newRelations.length > 0) {
      field.setValue(Object.values([...value, ...newRelations]));
      yjsField.insert(newRelations);
    }
    setIsUploading(false);
  };

  const openRelationModal = async (_: unknown, idx: number | null = null) => {
    const selected =
      idx === null
        ? value.reduce(
            (selected, relation) => {
              const split = relation.dataUrl.split('/');
              const id = split[split.length - 1];
              selected[id] = relation;
              return selected;
            },
            {} as Record<string, RelationWithoutHydration>,
          )
        : {};
    const isLocallyMultiple = isMultiple && idx === null;
    const oldValue = [...value];

    const newRelations: RelationWithoutHydration[] = await modal({
      title: (
        <div className="text-xl md:text-3xl">
          {t('ContentForm.Relation.PickObjects', { label })}
        </div>
      ),
      content: (
        <LinkObjectContentModal
          relationType={relationType}
          selected={selected}
          isMultiple={isLocallyMultiple}
          onMediaUpload={onMediaUpload}
          {...getTestProps(testId, 'link-modal', 'testId')}
        />
      ),
      size: '2xl',
      dialogAdditionalClasses: 'max-h-[calc(100vh-32px)]',
    });

    if (newRelations) {
      if (idx !== null) {
        const newRelation = Object.values(newRelations)[0];
        const newDatUrl = newRelation.dataUrl;
        if (oldValue.find((obj) => obj.dataUrl === newDatUrl)) {
          toast.error(t('ContentForm.Relation.AlreadyInArray'));
        } else {
          oldValue[idx] = newRelation;
          field.replaceValue(idx, newRelation);
          yjsField.replace(idx, newRelation);
        }
      } else {
        field.setValue(Object.values(newRelations));
        yjsField.update(Object.values(newRelations));
      }
    }

    setTimeout(onBlur);
  };

  const openCreateObjectModal = async () => {
    const pickedType = await modal({
      title: (
        <div className="text-xl md:text-3xl">
          {t('ContentForm.Relation.PickType')}
        </div>
      ),
      content: (
        <PickTypeContentModal
          relationType={relationType}
          {...getTestProps(testId, 'pick', 'testId')}
        />
      ),
      size: '2xl',
      buttons: [{ ...defaultButtonsProps.cancel, label: t('Global.Cancel') }],
    });

    if (!pickedType) return;

    const objectData = await modal({
      content: (
        <CreateObjectContentModal
          contentTypeName={pickedType}
          {...getTestProps(testId, 'create', 'testId')}
        />
      ),
      size: 'page',
      hideClose: true,
    });

    if (objectData) {
      const newRelation = {
        type: 'internal',
        dataUrl: `/api/v1/content/${pickedType}/${objectData.id}`,
      };
      field.pushValue(newRelation);
      yjsField.insert(newRelation);
    }
    setTimeout(onBlur);
    removeNewMedia();
  };

  const defaultParams = useMemo(
    () => ({ page: 1, limit: value.length }),
    [value.length],
  );

  const {
    data: relationsDict,
    isLoading: relationsAreLoading,
    reload: reloadRelations,
  } = useRelations(value, defaultParams);

  const { isPending: isChangingStatus, updateStatuses } = useObjectStatusChange(
    false,
    reloadRelations,
  );

  const unpublishedRelations =
    relationsAreLoading || !relationsDict
      ? []
      : Object.entries(relationsDict)
          .map(([contentTypeName, relations]) => ({
            contentTypeName,
            data:
              'data' in relations
                ? (relations.data.filter(
                    ({ internal }: ContentObject) =>
                      internal.status !== 'public',
                  ) as ContentObject[])
                : [],
            titleField:
              'titleField' in relations ? relations.titleField : undefined,
          }))
          .filter((acc) => acc.data.length > 0);

  const unpublishedRelationsFlat = unpublishedRelations.flatMap(
    ({ data }) => data,
  );

  const contentTypes = useMemo(
    () =>
      relationsAreLoading || !relationsDict
        ? []
        : Object.values(relationsDict).map(({ contentType }) => contentType),
    [relationsAreLoading, relationsDict],
  );

  const [featuredImages, featuredImagesLoading] =
    useFeaturedImages(contentTypes);

  const modalId = useId();

  const openEditModal = async (
    ctdName: string,
    id: string,
    val: RelationWithoutHydration,
    fieldName: string,
  ) => {
    if (awareness) {
      awareness.setLocalStateField('activeField', fieldName);
    }

    await modal({
      id: modalId,
      content: (
        <EditObjectContentModal
          contentTypeName={ctdName}
          id={id}
          onSubmit={({
            success,
            contentObject: relationContentObject,
            contentType: relationContentType,
            errors,
          }) => {
            FlotiqPlugins.run(
              'flotiq.form.relation::after-submit',
              new FormRelationAfterSubmitEvent({
                contentType: contentType,
                contentObject: initialData,
                fieldName,
                relation: val,
                relationContentObject,
                relationContentType,
                isSuccess: success,
                errors: errors,
                values: form.state.values,
              }),
            );
          }}
          {...getTestProps(testId, 'edit', 'testId')}
        />
      ),
      size: 'page',
      hideClose: true,
    });

    reloadRelations();

    if (awareness) {
      awareness.setLocalStateField('activeField', undefined);
    }
  };

  useEffect(() => {
    return () => {
      modal.close(modalId);
    };
  }, [modal, modalId]);

  const limitReached = useMemo(
    () => !isMultiple && value.length > 0,
    [isMultiple, value],
  );

  const deleteRelation = async (idx: number) => {
    const result = await modal.delete(t('ContentForm.RemoveItem'));
    if (!result) return;
    field.removeValue(idx);
    onBlur();
    yjsField.delete(idx);
  };

  const swapRelation = async (idx: number) => {
    openRelationModal(null, idx);
  };

  const globalError = useMemo(() => {
    if (!error) return;
    const parsedError = Array.isArray(error) ? error[0] : error;
    if (typeof parsedError === 'string') return error;
  }, [error]);

  const openFullSizeImage = useCallback<RelationShowFn>((media) => {
    window.open(getMediaUrl(media, 0, 0, false), '_blank');
  }, []);

  const handleAddVariant = useCallback(
    async (media: Media) => {
      if (!onVariantCreate) return;
      const [data, hasErrors] = await onVariantCreate(media);
      if (!hasErrors && data) reloadRelations();
    },
    [onVariantCreate, reloadRelations],
  );

  const handleDeleteVariant = useCallback(
    async (media: Media, idx: number) => {
      if (!onVariantDelete) return;

      modal.deleting('variant-delete');

      const [data, hasErrors] = await onVariantDelete(media, idx);
      if (!hasErrors && data) reloadRelations();
    },
    [modal, onVariantDelete, reloadRelations],
  );

  const onDeleteVariant = useCallback(
    async (media: Media, idx: number) => {
      await modal.delete(
        t('ContentForm.VariantDeleteConfirm'),
        'variant-delete',
        () => handleDeleteVariant(media, idx),
      );
    },
    [handleDeleteVariant, modal, t],
  );

  const handleChangeOrder = (sourceDataUrl: string, targetDataUrl: string) => {
    if (sourceDataUrl === targetDataUrl) return;

    const sourceIndex = value.findIndex(
      ({ dataUrl }) => dataUrl === sourceDataUrl,
    );
    const targetIndex = value.findIndex(
      ({ dataUrl }) => dataUrl === targetDataUrl,
    );

    if (sourceIndex >= 0 && targetIndex >= 0) {
      field.moveValue(sourceIndex, targetIndex);
      onBlur();
      yjsField.move(sourceIndex, targetIndex);
    }
  };

  const onDrop = useDebounceCallback(handleChangeOrder, 10);

  const publishAll = async () => {
    const publish = await modal({
      title: (
        <div className="font-bold text-3xl">
          {t('ObjectStatus.Relations.PublishTitle')}
        </div>
      ),
      content: (
        <>
          <p className="mb-5 text-lg">
            {t(`ObjectStatus.Relations.PublishContent`, {
              count: unpublishedRelationsFlat.length,
            })}
          </p>
          <div className="flex flex-col items-center space-y-2 mb-20 xs:mb-0">
            {unpublishedRelations.map(
              ({ contentTypeName, data, titleField }) => {
                const relationType = contentTypeName;
                return data.map((relation: string | ContentObject) => {
                  const relationData =
                    typeof relation === 'string' ? { id: relation } : relation;

                  return (
                    <RelationCard
                      key={`${relationType}-${relationData.id}`}
                      relation={relationData}
                      relationType={relationType}
                      featuredImageUrl={featuredImages[contentTypeName]}
                      featuredImageLoading={featuredImagesLoading}
                      titleField={titleField}
                      relationsAreLoading={relationsAreLoading}
                      withObjectStatus
                      testId={testId}
                    />
                  );
                });
              },
            )}
          </div>
        </>
      ),
      buttons: [
        {
          key: 'cancel',
          color: 'blueBordered',
          label: t('Global.Cancel'),
          result: false,
        },
        {
          key: 'publish',
          color: 'blue',
          label: t('ObjectStatus.Relations.PublishAll'),
          result: true,
        },
      ],
      size: 'lg',
      contentAdditionalClasses: 'pt-0',
    });
    if (!publish) return;

    updateStatuses({
      objects: unpublishedRelations.reduce(
        (acc, { contentTypeName, data }) => {
          acc[contentTypeName] = data.map((relation) => relation.id);
          return acc;
        },
        {} as Record<string, string[]>,
      ),
      action: 'publish',
    });
  };

  return (
    <div className={twMerge('relative group/relation', additionalClasses)}>
      <div className="flex gap-3 items-end justify-between mb-1.5">
        <label
          className="block text-sm text-slate-400 dark:text-slate-200"
          {...getTestProps(testId, 'label')}
        >
          {label}
          {required && <RequiredTemplate />}
        </label>
        {unpublishedRelationsFlat.length > 0 && (
          <div
            className="flex gap-3 items-center lg:opacity-0 group-hover/relation:opacity-100
                      group-focus-within/relation:opacity-100 transition-opacity"
          >
            <p className="dark:text-slate-200">
              {t('ObjectStatus.Relations.Changed', {
                count: unpublishedRelationsFlat.length,
              })}
            </p>
            <Button
              onClick={publishAll}
              buttonColor="slateBordered"
              disabled={isChangingStatus}
              iconImage={
                isChangingStatus ? (
                  <Loader size="small" type="spinner-grid" />
                ) : undefined
              }
              {...getTestProps(testId, 'publish-all', 'testId')}
            >
              {t('ObjectStatus.Relations.PublishAll')}
            </Button>
          </div>
        )}
      </div>
      {value.length > 0 && (
        <div
          className="flex flex-col gap-2 mb-2"
          {...getTestProps(testId, 'relations-container')}
        >
          <div className={twMerge('flex flex-col space-y-4')}>
            {relationsDict &&
              value.map((val, idx, currentValue) => {
                const [ctdName, id] = getObjectDataFromDataUrl(val.dataUrl);
                let relations: string[] | ContentObject[] = [];

                if (relationsDict?.[ctdName]) {
                  relations =
                    'data' in relationsDict[ctdName]
                      ? relationsDict[ctdName]?.data
                      : relationsDict[ctdName];
                }

                const relation = (relations || []).find((element) =>
                  isRecord(element) && 'id' in element
                    ? element.id === id
                    : element === id,
                );

                const relationToShow = isRecord(relation) ? relation : { id };
                return (
                  <div
                    key={`${ctdName}-${
                      typeof relationToShow === 'string'
                        ? relationToShow
                        : relationToShow.id
                    }`}
                  >
                    <form.Subscribe
                      selector={(state) =>
                        state.errors[0]?.[`${field.name}[${idx}].dataUrl`] as
                          ZodError[] | string
                      }
                    >
                      {(itemError) => (
                        <FieldEditorsIndicator
                          editors={yjsField.fieldEditors.filter(
                            ({ activeField }) =>
                              activeField === `${field.name}[${idx}]`,
                          )}
                        >
                          <DraggableRelationCard
                            onDrop={onDrop}
                            dataUrl={val.dataUrl}
                            relation={relationToShow}
                            relationType={ctdName}
                            titleField={
                              relationsDict?.[ctdName] &&
                              'titleField' in relationsDict[ctdName]
                                ? relationsDict[ctdName]?.titleField
                                : undefined
                            }
                            featuredImageUrl={featuredImages[ctdName]}
                            featuredImageLoading={featuredImagesLoading}
                            relationsAreLoading={relationsAreLoading}
                            relationsLength={currentValue.length}
                            onDelete={() => deleteRelation(idx)}
                            onEdit={
                              !relation
                                ? undefined
                                : () =>
                                    openEditModal(
                                      ctdName,
                                      id,
                                      val,
                                      `${field.name}[${idx}]`,
                                    )
                            }
                            onSwap={() => swapRelation(idx)}
                            onShow={openFullSizeImage}
                            disabled={actionsDisabled}
                            onVariantCreate={
                              !relation ? undefined : handleAddVariant
                            }
                            onVariantDelete={
                              !relation ? undefined : onDeleteVariant
                            }
                            additionalContainerClasses={
                              itemError || !relation ? 'border border-red' : ''
                            }
                            withObjectStatus
                            testId={testId}
                          />
                          <HelpErrorTextsTemplate
                            error={
                              typeof itemError === 'string'
                                ? itemError
                                : itemError?.map((er) => er.message)
                            }
                            {...getTestProps(
                              testId,
                              `${ctdName}-${id}`,
                              'testId',
                            )}
                          />
                        </FieldEditorsIndicator>
                      )}
                    </form.Subscribe>
                  </div>
                );
              })}
          </div>
        </div>
      )}
      {!limitReached && (
        <div
          className="flex flex-wrap gap-2 mb-2"
          {...getTestProps(testId, 'buttons-container')}
        >
          <Tooltip
            tooltip={
              canCreateRelation ? '' : t('ContentForm.Relation.CannotCreate')
            }
          >
            {hasOnlyMedia ? (
              <FileButton
                onUpload={handleUpload}
                multiple={isMultiple}
                disabled={actionsDisabled || !canCreateRelation}
                onBlur={onBlur}
                additionalClasses="w-fit"
                testId={testId}
              >
                {t('ContentForm.Relation.AddMedia')}
              </FileButton>
            ) : (
              <Button
                onClick={openCreateObjectModal}
                disabled={actionsDisabled || !canCreateRelation}
                {...getTestProps(testId, 'add-object', 'testId')}
              >
                {t('ContentForm.Relation.AddObject', {
                  label,
                })}
              </Button>
            )}
          </Tooltip>
          <Tooltip
            tooltip={
              canReadRelation ? '' : t('ContentForm.Relation.CannotRead')
            }
          >
            {hasOnlyMedia ? (
              <Button
                onClick={openRelationModal}
                buttonColor="blueBordered"
                onBlur={onBlur}
                disabled={isUploading || actionsDisabled || !canReadRelation}
                {...getTestProps(testId, 'link-media', 'testId')}
              >
                {t('ContentForm.Relation.LinkMedia')}
              </Button>
            ) : (
              <Button
                onClick={openRelationModal}
                buttonColor="blueBordered"
                onBlur={onBlur}
                disabled={actionsDisabled || !canReadRelation}
                {...getTestProps(testId, 'link-object', 'testId')}
              >
                {t('ContentForm.Relation.PickObjects', {
                  label,
                })}
              </Button>
            )}
          </Tooltip>
        </div>
      )}
      <HelpErrorTextsTemplate
        helpText={helpText}
        error={globalError}
        testId={testId}
      />
    </div>
  );
};

export default RelationField;
